'use client';

import type { JsonObject } from 'utils/json';
import type { Market } from './formatters';
import type {
  PurchaseSnapshot,
  TravelPurchaseSnapshot,
} from './usePolicySummary';
import { pushDataLayer } from '$/components/cms/pushDataLayer';
import { homePurchaseItemId } from '$/modules/tracking/homePurchaseItemId';
import type {
  PolicySummaryPurchase,
  TravelPolicySummary,
} from '$/types/policySummary';

// 7-day dedup window, matching the HubSpot success page's `<id>_gaTracking`
// cookie so re-opening the confirmation URL does not double-count the purchase.
const GA_DEDUP_COOKIE_DAYS = 7;

const hasCookie = (name: string): boolean =>
  typeof document !== 'undefined' &&
  document.cookie
    .split('; ')
    .some((entry) => entry.startsWith(`${encodeURIComponent(name)}=`));

const setCookieForDays = (name: string, value: string, days: number): void => {
  if (typeof document === 'undefined') return;
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
    value,
  )}; path=/; max-age=${maxAge}`;
};

// TI: the HubSpot success page's event, reproduced 1:1 (CMS-1794).
const buildTravelPurchaseStatus = ({
  applicationId,
  snapshot,
  data,
  currency,
}: {
  applicationId: string;
  snapshot: TravelPurchaseSnapshot;
  data: TravelPolicySummary;
  currency: string;
}): JsonObject => {
  const discount = data.basePremium - data.collectionAmount;

  return {
    event: 'purchase_status',
    transaction_id: data.policyNo || applicationId,
    status: 'success',
    error_message: '',
    value: Number.isFinite(data.collectionAmount)
      ? data.collectionAmount.toString()
      : '',
    currency,
    marketing_consent: snapshot.marketingConsent ?? '',
    coupon: '',
    search_result_parameter: snapshot.searchResultParameters ?? '',
    vertical_code: 'TI',
    items: [
      {
        item_id: data.productCode ?? '',
        item_name: data.productName ?? '',
        item_brand: data.carrierName ?? '',
        deal_status: true,
        promotion: '',
        price: Number.isFinite(data.collectionAmount)
          ? data.collectionAmount
          : 0,
        currency,
        // Guarded like `value` / `price` above: either amount being non-finite
        // would otherwise push the string 'NaN' into the analytics payload.
        discount: Number.isFinite(discount) ? discount.toString() : '',
        item_variant: '',
        product_details: '',
      },
    ],
    user_data: (snapshot.userData ?? {}) as JsonObject,
    userIdentities: (snapshot.userIdentities ?? {}) as JsonObject,
  };
};

// item_id shared by the page <meta> and the event: BE's product code for TI
// (HubSpot parity), the uid the checkout persisted for HI.
export const purchaseItemId = (data: PolicySummaryPurchase): string =>
  data.vertical === 'HI'
    ? homePurchaseItemId(data, data)
    : (data.productCode ?? '');

const buildPurchaseStatus = async ({
  applicationId,
  snapshot,
  data,
  currency,
}: {
  applicationId: string;
  snapshot: PurchaseSnapshot;
  data: PolicySummaryPurchase;
  currency: string;
}): Promise<JsonObject | undefined> => {
  // The resolver stamps `data.vertical` from the snapshot, so the two always
  // agree; the inner check narrows the snapshot without a cast.
  switch (data.vertical) {
    case 'TI':
      return snapshot.vertical === 'TI'
        ? buildTravelPurchaseStatus({ applicationId, snapshot, data, currency })
        : undefined;
    case 'HI': {
      if (snapshot.vertical !== 'HI') return undefined;
      // Dynamic import: the builder needs crypto-js / js-md5 to hash user_data,
      // and this module ships in every CMS page's client bundle.
      const { buildHomePurchaseStatus } = await import(
        '$/modules/tracking/homePurchaseStatus'
      );
      return buildHomePurchaseStatus({
        applicationId,
        purchase: snapshot,
        summary: data,
        currency,
      });
    }
    default:
      data satisfies never;
      return undefined;
  }
};

/**
 * Fire the GA4 `purchase_status` event once per application id (7-day cookie).
 * Client-only (document.cookie + `pushDataLayer`); call once the purchase has
 * resolved.
 */
export const firePurchaseStatusEvent = async (params: {
  applicationId: string;
  snapshot: PurchaseSnapshot;
  data: PolicySummaryPurchase;
  market: Market;
}): Promise<void> => {
  const { applicationId, snapshot, data, market } = params;

  const dedupKey = `${applicationId}_gaTracking`;
  if (hasCookie(dedupKey)) return;

  const currency = market === 'singsaver' ? 'SGD' : 'HKD';
  const event = await buildPurchaseStatus({
    applicationId,
    snapshot,
    data,
    currency,
  });
  if (!event) return;

  pushDataLayer(event);

  setCookieForDays(dedupKey, 'true', GA_DEDUP_COOKIE_DAYS);
};
